import type { CustomSectionDef } from '@/features/ara-generation/types/ara-configure'

export type { CustomSectionDef } from '@/features/ara-generation/types/ara-configure'

export type AraSectionKey = string

export type AraSectionCatalogEntry = {
  key: AraSectionKey
  labelKey: string
  baselineKey: string
  /** Excluded from the default selection; the SE has to choose it. */
  optIn?: boolean
}

export const ARA_SECTION_CATALOG: AraSectionCatalogEntry[] = [
  {
    key: 'overview',
    labelKey: 'sections.overview',
    baselineKey: 'sections.baselines.overview'
  },
  {
    key: 'scoped_assets',
    labelKey: 'sections.scoped_assets',
    baselineKey: 'sections.baselines.scoped_assets'
  },
  {
    key: 'failure_modes',
    labelKey: 'sections.failure_modes',
    baselineKey: 'sections.baselines.failure_modes'
  },
  {
    key: 'social_proof',
    labelKey: 'sections.social_proof',
    baselineKey: 'sections.baselines.social_proof'
  },
  {
    key: 'asset_risk_detail',
    labelKey: 'sections.asset_risk_detail',
    baselineKey: 'sections.baselines.asset_risk_detail'
  },
  {
    key: 'peer_positioning',
    labelKey: 'sections.peer_positioning',
    baselineKey: 'sections.baselines.peer_positioning',
    /**
     * Off unless the SE asks for it, for the same reason as `oem_landscape` below:
     * its closing line recommends standardising new units on the cohort's best
     * manufacturer, which is the same brand-swap argument. This card shipped inside
     * Overview until Oct 2026, where no picker could reach it — SEs reported it as
     * confusing in front of a customer and better suited to a pilot review.
     */
    optIn: true
  },
  {
    key: 'oem_landscape',
    labelKey: 'sections.oem_landscape',
    baselineKey: 'sections.baselines.oem_landscape',
    /**
     * Off unless the SE asks for it. The backend excludes opt-in sections from the
     * default set (catalog `opt_in`), so a checked box here would promise a section
     * that never arrives. Recommending brand swaps works against the partnership an
     * SE is building, and SEs were deleting this section by hand (SCP-114).
     */
    optIn: true
  }
]

export const ARA_BUILTIN_KEYS = ARA_SECTION_CATALOG.map(entry => entry.key)

/**
 * The sections a fresh configure starts with: every builtin EXCEPT the opt-in ones.
 * Mirrors the backend's default set, so the picker and the report agree about what
 * `sections: null` produces.
 */
export const ARA_DEFAULT_SECTION_KEYS = ARA_SECTION_CATALOG.filter(
  entry => !entry.optIn
).map(entry => entry.key)

export const BUILTIN_SECTION_KEY_SET: Set<string> = new Set(ARA_BUILTIN_KEYS)

const LEGACY_BUILTIN_KEY_ALIASES: Record<string, string> = {
  next_steps: 'scoped_assets',
  oem_analysis: 'oem_landscape'
}
const LEGACY_BUILTIN_KEY_SET = new Set(Object.keys(LEGACY_BUILTIN_KEY_ALIASES))

const LEGACY_DEFAULT_SECTION_ORDER = [
  'overview',
  'social_proof',
  'failure_modes',
  'asset_risk_detail',
  'oem_analysis',
  'next_steps'
]

export function canonicalizeAraSectionKey(
  key: string,
  customSectionKeyReplacements?: ReadonlyMap<string, string>
): string {
  const customReplacement = customSectionKeyReplacements?.get(key)
  if (customReplacement !== undefined) return customReplacement
  return Object.prototype.hasOwnProperty.call(LEGACY_BUILTIN_KEY_ALIASES, key)
    ? LEGACY_BUILTIN_KEY_ALIASES[key]
    : key
}

export function canonicalizeAraSectionKeys(
  keys: string[],
  customSectionKeyReplacements?: ReadonlyMap<string, string>
): string[] {
  const seen = new Set<string>()
  return keys
    .map(key => canonicalizeAraSectionKey(key, customSectionKeyReplacements))
    .filter(key => {
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

export function canonicalizeAraSectionOrder(
  keys: string[],
  customSectionKeyReplacements?: ReadonlyMap<string, string>
): string[] {
  const hasLegacyDefaultPrefix =
    keys.length >= LEGACY_DEFAULT_SECTION_ORDER.length &&
    LEGACY_DEFAULT_SECTION_ORDER.every((key, index) => keys[index] === key)

  if (hasLegacyDefaultPrefix) {
    return [
      ...ARA_BUILTIN_KEYS,
      ...canonicalizeAraSectionKeys(
        keys.slice(LEGACY_DEFAULT_SECTION_ORDER.length),
        customSectionKeyReplacements
      ).filter(key => !BUILTIN_SECTION_KEY_SET.has(key))
    ]
  }

  return canonicalizeAraSectionKeys(keys, customSectionKeyReplacements)
}

export function migrateAraCustomSections(customSections: CustomSectionDef[]) {
  const canonicalKeys = customSections.map(section => canonicalizeAraSectionKey(section.key))
  const reservedKeys = new Set([...ARA_BUILTIN_KEYS, ...LEGACY_BUILTIN_KEY_SET, ...canonicalKeys])
  const keyReplacements = new Map<string, string>()

  const migratedCustomSections = customSections.map((section, index) => {
    const canonicalKey = canonicalKeys[index]
    if (!BUILTIN_SECTION_KEY_SET.has(canonicalKey)) {
      return { ...section, key: canonicalKey }
    }

    const base = canonicalKey + '_custom'
    let key = base
    let suffix = 2
    while (reservedKeys.has(key)) {
      key = base + '_' + suffix
      suffix += 1
    }
    reservedKeys.add(key)
    // Keep legacy built-in aliases distinct from a custom key that matches their new name.
    keyReplacements.set(section.key, key)
    return { ...section, key }
  })

  return { customSections: migratedCustomSections, keyReplacements }
}

const SLUG_MAX_LENGTH = 60

export function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, SLUG_MAX_LENGTH)
}

export function detectCollision(key: string, existingKeys: string[]): boolean {
  if (BUILTIN_SECTION_KEY_SET.has(key) || LEGACY_BUILTIN_KEY_SET.has(key)) return true
  return existingKeys.includes(key)
}
